// Block trade deal book — broker-quoted blocks operators can act on.
// Mirrors fms_inherited.models.BlockTradeDetails on the backend.

import { RawRecommendation } from '@/components/order_recommendation/recommendationSelectors';

export const BLOCK_TRADE_PARTY_TYPE = {
  BUYER: 1,
  SELLER: 2,
} as const;

export type BlockTradePartyType =
  (typeof BLOCK_TRADE_PARTY_TYPE)[keyof typeof BLOCK_TRADE_PARTY_TYPE];

export const BLOCK_TRADE_STATUS = {
  AVAILABLE: 1,
  EXPIRED: 2,
} as const;

export type BlockTradeStatus =
  (typeof BLOCK_TRADE_STATUS)[keyof typeof BLOCK_TRADE_STATUS];

export const BLOCK_TRADE_PARTY_TYPE_OPTIONS: { value: BlockTradePartyType; label: string }[] = [
  { value: BLOCK_TRADE_PARTY_TYPE.BUYER, label: 'Buyer' },
  { value: BLOCK_TRADE_PARTY_TYPE.SELLER, label: 'Seller' },
];

export const BLOCK_TRADE_STATUS_OPTIONS: { value: BlockTradeStatus; label: string }[] = [
  { value: BLOCK_TRADE_STATUS.AVAILABLE, label: 'Available' },
  { value: BLOCK_TRADE_STATUS.EXPIRED, label: 'Expired' },
];

/** One snapshot figure. `formula` is present on derived values, `source` on fetched ones.
 *  `value` is null when the figure could not be sourced — never treat null as zero. */
export interface BlockTradeMetric {
  value: number | null;
  formula?: string;
  source?: string;
  /** True when the desk typed this number in rather than the system sourcing it.
   *  Must always be shown — a typed figure presented like a sourced one is how a desk
   *  ends up trading on a guess. */
  manual?: boolean;
}

/** The three desk inputs an operator may type in. Everything else on the snapshot is a
 *  fact the system sources; only these are judgement calls a dealer owns. */
export const OVERRIDABLE_METRICS = ['limit_price', 'mandate_qty', 'target_position_pct'] as const;

export type OverridableMetric = (typeof OVERRIDABLE_METRICS)[number];

/** One typed-in figure, as stored on the snapshot. */
export interface BlockTradeOverride {
  value: number;
  /** How the page names the figure this stands in for. */
  label?: string;
  /** What the system would have shown — null when it could not source it at all. */
  replaced?: number | null;
  note?: string | null;
}

/** What the operator sends: the complete set. A field sent as null, or left out, is cleared. */
export type BlockTradeOverridePayload = Partial<
  Record<OverridableMetric, { value: string | number; note?: string } | null>
>;

/** A figure the backend could not source, with the reason to show the desk. */
export interface BlockTradeUnavailable {
  field: string;
  reason: string;
}

/** The raw OMS recommendation bundle as at the moment the snapshot was captured.
 *
 *  Implied returns are deliberately NOT precomputed on the backend. This page derives
 *  them from this array with the same `recommendationSelectors` helpers the Orders &
 *  Execution screen uses, so the two screens cannot quote different numbers for the
 *  same analyst target. */
export interface BlockTradeRecommendationContext {
  recommendations: RawRecommendation[];
  /** Who the company is assigned to now. */
  analyst_name: string | null;
  ic_user_name: string | null;
  /** Who gave the latest recommendation in each role — what the rows are matched on, so
   *  a reassignment does not blank the figures. */
  latest_rec_analyst_name: string | null;
  latest_rec_ic_user_name: string | null;
}

export interface BlockTradeSnapshot {
  captured_at: string;
  /** Our side of the block: a broker holding a SELLER is offering stock to us. */
  our_side: 'BUY' | 'SELL';
  /** What the desk calls the quoted price: "Ask Price" when the broker is selling to us,
   *  "Bid Price" when the broker is buying from us. */
  quoted_price_label: string;
  /** Investment strategies the figures below were measured against. At least one is
   *  required on save; empty only on rows captured before that rule, where the figures
   *  cover the whole firm. */
  investment_strategy_codes: string[];
  block_deal_offer: {
    block_deal_value: BlockTradeMetric;
  };
  desk_mandate: {
    limit_price: BlockTradeMetric;
    mandate_qty: BlockTradeMetric;
    /** How much of the mandate has filled. Null when no execution is recorded at all,
     *  which `unexecuted_qty` reads as nothing filled rather than as unknown. */
    executed_qty: BlockTradeMetric;
    unexecuted_qty: BlockTradeMetric;
    /** Struck at the desk limit only — null when the tracker carries no limit price.
     *  CMP is never substituted; it would answer a different question. */
    mandate_value: BlockTradeMetric;
    target_position_pct: BlockTradeMetric;
    cmp: BlockTradeMetric;
    cmp_timestamp: string | null;
    block_discount_premium_pct: BlockTradeMetric;
  };
  portfolio: {
    holding_qty: BlockTradeMetric;
    total_aum: BlockTradeMetric;
    investment_cost: BlockTradeMetric;
    current_value: BlockTradeMetric;
    cost_price: BlockTradeMetric;
    unrealized_pnl_pct: BlockTradeMetric;
    current_holding_pct: BlockTradeMetric;
    target_position_pct: BlockTradeMetric;
    combined_holding_pct_post_block: BlockTradeMetric;
    post_trade_remaining_qty: BlockTradeMetric;
    /** Buy side only. */
    new_combined_avg_cost_post_block?: BlockTradeMetric;
    /** Sell side only. */
    realized_pnl_on_block?: BlockTradeMetric;
  };
  liquidity: {
    adv_21d: BlockTradeMetric;
    /** Mean daily traded value (volume × mid price) over the same 21 sessions. */
    avg_value_21d: BlockTradeMetric;
    avg_price_21d: BlockTradeMetric;
    days_of_adv_handled_by_block: BlockTradeMetric;
  };
  /** Null when the OMS had no recommendation for this company at capture time. */
  recommendation_context: BlockTradeRecommendationContext | null;
  /** Figures the desk typed in, keyed by field. Empty when everything was sourced. */
  manual_overrides: Partial<Record<OverridableMetric, BlockTradeOverride>>;
  unavailable: BlockTradeUnavailable[];
}

/** Free-form per-deal details. Holds the desk snapshot once one has been captured;
 *  an older row created before snapshots may hold `{}` or arbitrary keys. */
export type BlockTradeAdditionalDetails = Partial<BlockTradeSnapshot> & Record<string, unknown>;

/** Narrow `additional_details` to a usable snapshot, or null when none was captured.
 *  `captured_at` is the marker: it is stamped by the snapshot builder and by nothing else,
 *  so an older row holding only operator-supplied keys correctly reads as "no snapshot". */
export const asSnapshot = (
  details: BlockTradeAdditionalDetails | null | undefined,
): BlockTradeSnapshot | null => {
  if (!details || typeof details !== 'object' || !('captured_at' in details)) return null;
  const snapshot = details as unknown as BlockTradeSnapshot;
  // Every other field is read through optional chaining, so a missing group degrades to a
  // blank figure. `unavailable` is the one field consumers iterate directly, so guarantee
  // the array the type promises rather than trusting the cast. Same reference back when the
  // payload is well formed — callers pass nested fields to effect dependency arrays.
  return Array.isArray(snapshot.unavailable) ? snapshot : { ...snapshot, unavailable: [] };
};

/** An investment strategy the block is being judged against. */
export interface BlockTradeInvestmentStrategy {
  id: number;
  code: string;
  name: string;
}

export interface BlockTrade {
  id: number;
  trade_date: string;
  status: BlockTradeStatus;
  status_display: string;
  company: number;
  company_name: string;
  company_symbol: string;
  broker: number;
  broker_name: string;
  party_type: BlockTradePartyType;
  party_type_display: string;
  /** Which side the desk is on — derived from party_type by the backend. */
  our_side: 'BUY' | 'SELL';
  /** What the desk calls `ask_price`: "Ask Price" when the broker is selling to us,
   *  "Bid Price" when the broker is buying from us. */
  quoted_price_label: string;
  investment_strategies: number[];
  investment_strategy_details: BlockTradeInvestmentStrategy[];
  quantity: number;
  /** Null when the broker quoted a size but no price — unpriced, never zero. */
  ask_price: number | null;
  additional_details: BlockTradeAdditionalDetails;
  is_active: boolean;
  created_ts: string;
  updated_ts: string;
}

/** The price label for a side, for use before a row exists — i.e. in the create form,
 *  where the backend has not yet derived `quoted_price_label`. Mirrors
 *  `BlockTradeDetails.quoted_price_label`: the quote is named for the side that made it,
 *  so a broker selling to us quotes an ask and a broker buying from us quotes a bid.
 *  With no side chosen yet the price has no side-specific name, so it stays generic. */
export const quotedPriceLabelFor = (partyType: BlockTradePartyType | null): string => {
  if (partyType === BLOCK_TRADE_PARTY_TYPE.SELLER) return 'Ask Price';
  if (partyType === BLOCK_TRADE_PARTY_TYPE.BUYER) return 'Bid Price';
  return 'Quoted Price';
};

/** Create payload. `trade_date` and `status` are omitted so the backend defaults
 *  (today / Available) apply — see BlockTradeDetails model defaults. */
export interface BlockTradeCreatePayload {
  company: number;
  broker: number;
  party_type: BlockTradePartyType;
  /** InvestmentStrategy PKs. At least one is required — the backend rejects an empty list. */
  investment_strategies: number[];
  quantity: number;
  ask_price: string;
  additional_details?: BlockTradeAdditionalDetails;
  trade_date?: string;
  status?: BlockTradeStatus;
}

export type BlockTradeUpdatePayload = Partial<BlockTradeCreatePayload>;

/** One label/value pair on the chat card, formatted by this screen. `value` is a rendered
 *  string, never a number — an unavailable figure carries its dash, never a zero. */
export interface BlockTradeChatFigure {
  label: string;
  value: string;
  /** The desk typed this figure in rather than the system sourcing it. */
  manual?: boolean;
}

export interface BlockTradeChatSection {
  header: string;
  figures: BlockTradeChatFigure[];
}

/** What the backend needs to assemble the Google Chat card. Figures are already formatted;
 *  the server decides layout only, so chat and screen cannot quote a block differently. */
export interface BlockTradeChatSharePayload {
  title: string;
  subtitle: string;
  byline: string;
  sections: BlockTradeChatSection[];
  footer: string;
}

export interface BlockTradeChatShareResponse {
  detail: string;
}
